import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { FileType } from "../../models/audit/auditSession";
import { type AnalyticsOwner } from "../../lib/analyticsOwner";
import {
  AnalyticsWorkflowError,
  type AnalyticsFailureKind,
} from "../../lib/analyticsWorkflow";
import { type AnalyticsRunOutputSummary } from "../../models/audit/analyticsWorkflow";
import { useAnalyticsOutputDownloadUrl } from "../useAnalyticsWorkflows";

/**
 * Why an output file is refused where the pane is open over a whole column.
 *
 * Both the row's buttons and the handlers below refuse the same case, and one
 * refusal owes the auditor one sentence.
 */
export const OUTPUT_FILE_NEEDS_A_ROW =
  "Open this file from a sample row to view or download it.";

/**
 * What a failed fetch is told to the auditor, per failure kind.
 *
 * Authored against the kind rather than lifted from the error, for the reason
 * the verdict read's copy is: what the route or the request that failed had to
 * say is written for whoever has to fix it, and it reaches this toast verbatim
 * — the typed failure carries the API's own sentence where there is one and a
 * status line where there is not. Keyed by the kind union, so a kind added to
 * it fails to compile here rather than falling through to the generic line.
 *
 * Worded for a fetch rather than a download: viewing and downloading mint the
 * same signed URL, and either can be the press that fails.
 */
const OUTPUT_FILE_ERROR_MESSAGES: Record<AnalyticsFailureKind, string> = {
  workflow_deleted:
    "The workflow that published this file has been deleted, so the file can no longer be fetched.",
  not_found:
    "This output file could not be fetched because something it needs could not be found.",
  permission_denied:
    "Access to this output file was refused, so it could not be fetched.",
  // The conflict kind is the analytics output-download 409, which has two
  // producers — a disabled Optro Analytics credential and a workspace with none
  // at all. This line may assert only what holds under both: that no working
  // credential is in place and the remedy is an administrator's, not a retry. It
  // is held to read as the same message the needs-setup banner states for that
  // condition, in the register of a file that could not be fetched.
  conflict:
    "This output file could not be fetched because this workspace has no active Optro Analytics credential. An administrator has to make one available; there is nothing to retry until they do.",
  rejected: "This request was refused, so the file was not fetched.",
  session_expired:
    "Your session ended before the file was fetched. Sign in again to continue.",
  upstream_unavailable:
    "The analytics service is unavailable right now, so this file could not be fetched.",
  unreachable:
    "This file could not be fetched because the request never reached the server.",
  unreadable:
    "The link for this file could not be read, so the file was not fetched.",
  unknown: "This output file could not be fetched.",
};

/** The formats Midship's spreadsheet viewers draw: the CSV grid and the Excel viewer. */
const SPREADSHEET_FILE_TYPES: ReadonlySet<FileType> = new Set<FileType>([
  "csv",
  "xlsx",
  "xlsm",
]);

/**
 * The viewer type for an output, or null where Midship has no viewer for it.
 *
 * The manifest names the format as AuditBoard reported it, so it is normalised
 * and parsed rather than trusted to be one of Midship's own file types.
 */
export function viewableOutputFileType(
  output: AnalyticsRunOutputSummary,
): FileType | null {
  const parsed = FileType.safeParse(
    output.fileFormat.trim().toLowerCase().replace(/^\./, ""),
  );
  return parsed.success && SPREADSHEET_FILE_TYPES.has(parsed.data)
    ? parsed.data
    : null;
}

// Caps the whole transfer, which otherwise holds the file's View and Download
// buttons disabled for as long as it runs. The signed URL must outlive the cap,
// since the fallback reuses it.
const OUTPUT_DOWNLOAD_TIMEOUT_MS = 120_000;

/** Clicks a link to `href`, naming the saved file `fileName` where one is given. */
function clickDownloadLink(href: string, fileName: string | null) {
  const link = document.createElement("a");
  link.href = href;
  if (fileName !== null) {
    link.download = fileName;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** An output open in the viewer, with the signed URL minted to open it. */
export interface ViewedAnalyticsOutput {
  output: AnalyticsRunOutputSummary;
  fileType: FileType;
  url: string;
}

/**
 * Viewing and downloading a run's published outputs.
 *
 * **Both actions mint a fresh URL on the press.** A published output is never
 * copied into Midship and never registered as a document, so the only way to
 * its bytes is a signed URL minted against a row's run — and that URL is a
 * one-off grant, never held longer than the action that asked for it.
 *
 * **Downloading reads the bytes and saves them under the manifest's name.**
 * The signed URL is cross-origin, and browsers ignore `download` on a
 * cross-origin link and take the name the storage URL serves, which is
 * AuditBoard's current name for the file rather than the one the preview
 * shows. A same-origin blob URL lets the browser save the bytes under the
 * manifest's name. Where storage refuses the cross-origin read or the transfer
 * hits its time limit, the download falls back to the plain link and saves
 * under AuditBoard's name.
 * Viewing reads the same bytes through the viewers every other spreadsheet in
 * Midship opens in.
 */
export function useAnalyticsOutputFiles(
  owner: AnalyticsOwner | undefined,
  columnId: string,
  sourceRowId: string | undefined,
) {
  const mintOutputDownloadUrl = useAnalyticsOutputDownloadUrl(owner);
  const [pendingFileIds, setPendingFileIds] = useState<ReadonlySet<string>>(
    new Set(),
  );
  // Open and contents are held apart for two closes that must not drop the
  // file at once: the dialog keeps drawing it through its exit animation, and
  // a step picked over the pane closes the file without dropping it, so focus
  // stays on the step's row rather than being handed back to View. The
  // contents are dropped only by `viewerClosed`.
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewed, setViewed] = useState<ViewedAnalyticsOutput | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const withSignedUrl = useCallback(
    async (
      output: AnalyticsRunOutputSummary,
      use: (url: string) => void | Promise<void>,
    ) => {
      // The URL is minted against a row's run, and a column-level selection
      // names no row. The buttons are disabled where this holds, so this is the
      // last line of defence and not what an auditor meets — and it answers
      // rather than discarding the click, because a button that does nothing
      // at all is indistinguishable from a broken one.
      if (!sourceRowId) {
        toast.error(OUTPUT_FILE_NEEDS_A_ROW);
        return;
      }

      setPendingFileIds((prev) => new Set(prev).add(output.fileId));
      try {
        await use(
          await mintOutputDownloadUrl({
            fileId: output.fileId,
            columnId,
            sourceRowId,
          }),
        );
      } catch (fetchError) {
        // Anything that throws in here without naming a failure kind is this
        // build talking to itself — a missing id, a mint that answered
        // something unreadable — and takes the generic line rather than its
        // own sentence.
        toast.error(
          fetchError instanceof AnalyticsWorkflowError
            ? OUTPUT_FILE_ERROR_MESSAGES[fetchError.kind]
            : OUTPUT_FILE_ERROR_MESSAGES.unknown,
        );
      } finally {
        setPendingFileIds((prev) => {
          const next = new Set(prev);
          next.delete(output.fileId);
          return next;
        });
      }
    },
    [mintOutputDownloadUrl, columnId, sourceRowId],
  );

  const download = useCallback(
    (output: AnalyticsRunOutputSummary) =>
      withSignedUrl(output, async (url) => {
        const fileName = output.fileName.trim() === "" ? null : output.fileName;
        // With an empty recorded name there is nothing for the bytes to be
        // saved under, so the browser is handed the link and names the file.
        if (fileName === null) {
          clickDownloadLink(url, null);
          return;
        }
        // The read was refused by CORS, failed, or hit the time limit. A plain
        // link is not subject to CORS, and storage serves the signed URL as an
        // attachment, so the file still saves, under AuditBoard's name.
        const fallBackToLink = (error: unknown) => {
          console.warn(
            "Analytics output download fell back to the signed link",
            error,
          );
          clickDownloadLink(url, fileName);
        };
        const signal = AbortSignal.timeout(OUTPUT_DOWNLOAD_TIMEOUT_MS);
        let response: Response;
        try {
          response = await fetch(url, { signal });
        } catch (error) {
          fallBackToLink(error);
          return;
        }
        if (!response.ok) {
          throw new AnalyticsWorkflowError("unknown", response.status, null);
        }
        let blob: Blob;
        try {
          blob = await response.blob();
        } catch (error) {
          fallBackToLink(error);
          return;
        }
        const objectUrl = URL.createObjectURL(blob);
        clickDownloadLink(objectUrl, fileName);
        // Revoking too soon can cut the download short in some browsers; the
        // delay only holds the bytes in memory a little longer.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
      }),
    [withSignedUrl],
  );

  // Every press of View draws a new token and only the latest may open the
  // viewer. Only the pressed file's buttons are disabled while its URL is
  // minted, so presses on two files overlap, and without this the slower
  // answer would replace the file the auditor asked for last, or reopen a
  // viewer they had already closed.
  const latestViewRef = useRef(0);

  // A URL minted for one row's run must not open over another's, so a change
  // of row, column or owner abandons any view still being minted and closes
  // the viewer, whose Download would otherwise mint against the new row. The
  // cleanup also runs on unmount, where the close is a no-op.
  useEffect(
    () => () => {
      latestViewRef.current += 1;
      setViewerOpen(false);
    },
    [mintOutputDownloadUrl, columnId, sourceRowId],
  );

  const view = useCallback(
    (output: AnalyticsRunOutputSummary, trigger: HTMLElement) => {
      const fileType = viewableOutputFileType(output);
      // The row offers View only for these types, so this guards the handler
      // rather than anything an auditor can reach.
      if (fileType === null) {
        return Promise.resolve();
      }
      latestViewRef.current += 1;
      const token = latestViewRef.current;
      return withSignedUrl(output, (url) => {
        if (token !== latestViewRef.current) {
          return;
        }
        returnFocusRef.current = trigger;
        setViewed({ output, fileType, url });
        setViewerOpen(true);
      });
    },
    [withSignedUrl],
  );

  const closeViewer = useCallback(() => {
    latestViewRef.current += 1;
    setViewerOpen(false);
  }, []);

  // Drops the viewed file and hands focus back to the View button that opened
  // it. Called once the viewer has finished closing: after the dialog's exit
  // animation, or as soon as an open dialog is unmounted (the shared dialog
  // suppresses Radix's focus return, and there is no Radix trigger to return
  // to), or at once when the pane's own close button is pressed, since the pane
  // has no exit animation.
  const viewerClosed = useCallback(() => {
    const returnFocusTo = returnFocusRef.current;
    returnFocusRef.current = null;
    setViewed(null);
    if (returnFocusTo?.isConnected) {
      returnFocusTo.focus();
    }
  }, []);

  return {
    pendingFileIds,
    download,
    view,
    viewed,
    viewerOpen,
    closeViewer,
    viewerClosed,
  };
}

export type AnalyticsOutputFiles = ReturnType<typeof useAnalyticsOutputFiles>;
